import React, { useState } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, Upload, message } from "antd";
import { InboxOutlined, FilePdfOutlined } from "@ant-design/icons";
import { ORDER_ITEM_STATUS_OPTIONS } from "../../constants/statusOptions";
import { UNIT_CODE_OPTIONS } from "../../constants/invoiceConstants";

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

const UpdateOrderItemModal = ({
  show,
  onClose,
  item,
  onChange,
  onSubmit,
  statusOptions = ORDER_ITEM_STATUS_OPTIONS,
  employees,
  loadingEmployees
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleInputChange = (name, value) => {
    onChange({
      target: {
        name,
        value,
      },
    });
  };

  const handleFileChange = ({ fileList }) => {
    // Limit to only one file
    const newFileList = fileList.slice(-1);
    setFileList(newFileList);
    
    // Pass the file to parent component
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      handleInputChange("file", file);
    } else {
      handleInputChange("file", null);
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      await onSubmit(fileList.length > 0 ? fileList[0].originFileObj : null);
      setFileList([]);
    } catch (error) {
      console.error("Sipariş kalemini güncellerken hata meydana geldi:", error);
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    const isPDF = file.type === 'application/pdf';
    if (!isPDF) {
      message.error('Yalnızca PDF dosyası yükleyebilirsin!');
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Dosya boyutu en fazla 10MB olmalıdır');
    }
    
    return isPDF && isLt10M;
  };

  // Render file icon or placeholder
  const renderFileIcon = () => {
    if (item.hasFile) {
      return (
        <div className="flex items-center mb-4">
          <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
          <span className="ml-2">Current file: {item.fileName || 'Document.pdf'}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <Modal
      title="Sipariş Kalemi Düzenle"
      open={show}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          İptal
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={uploading} 
          onClick={handleSubmit}
        >
          Güncelle
        </Button>
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Kalem İsmi" required>
          <Input
            name="itemName"
            value={item.itemName}
            onChange={(e) => onChange(e)}
            placeholder="Kalem ismini giriniz"
          />
        </Form.Item>
        <Form.Item label="Adet" required>
          <InputNumber
            name="quantity"
            value={item.quantity}
            onChange={(value) => handleInputChange("quantity", value)}
            min={1}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="Birim Fiyat" required>
          <InputNumber
            name="unitPrice"
            value={item.unitPrice}
            onChange={(value) => handleInputChange("unitPrice", value)}
            min={0}
            step={0.01}
            formatter={(value) => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(value) => value.replace(/₺\s?|(,*)/g, "")}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="KDV Oranı (%)">
          <InputNumber
            name="taxRate"
            value={item.taxRate}
            onChange={(value) => handleInputChange("taxRate", value)}
            min={0}
            max={100}
            step={1}
            addonAfter="%"
            style={{ width: "100%" }}
            placeholder="18"
          />
        </Form.Item>
        <Form.Item label="Birim">
          <Select
            value={item.unitCode}
            onChange={(value) => handleInputChange("unitCode", value)}
            placeholder="Birim seçiniz"
            allowClear
            options={UNIT_CODE_OPTIONS}
          />
        </Form.Item>
        <Form.Item label="Durum">
          <Select
            name="status"
            value={item.status}
            onChange={(value) => handleInputChange("status", value)}
            style={{ width: "100%" }}
          >
            {statusOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Sorumlu Personel">
          <Select
            loading={loadingEmployees}
            name="assignedEmployeeId"
            value={item.assignedEmployeeId || (item.assignedEmployee ? item.assignedEmployee.id : undefined)}
            onChange={(value) => handleInputChange("assignedEmployeeId", value)}
            placeholder="Personel seçiniz"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            allowClear
          >
            {employees.map((employee) => (
              <Option
                key={employee.id}
                value={employee.id}
                label={`${employee.name} ${employee.surname}`}
              >
                {employee.name} {employee.surname}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Notlar">
          <TextArea
            name="notes"
            value={item.notes}
            onChange={(e) => onChange(e)}
            placeholder="Notlarınızı ekleyin"
            rows={4}
          />
        </Form.Item>

        <Form.Item label="PDF Dosya">
          {renderFileIcon()}
          <Dragger
            name="file"
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={beforeUpload}
            maxCount={1}
            accept=".pdf"
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">PDF dosyası yüklemek için tıklayın veya sürükleyin</p>
            <p className="ant-upload-hint">Sadece PDF dosyaları desteklenir (max. 10MB)</p>
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateOrderItemModal;